import { createFileRoute, Link } from '@tanstack/react-router'
import { Bot, Cpu, ShieldCheck, Lock } from 'lucide-react'
import AiChat from '@/components/ops/AiChat'
import AiUsageWidget from '@/components/ai-usage/AiUsageWidget'
import AiAuditLogWidget from '@/components/ai-usage/AiAuditLogWidget'
import ReviewAutomationWidget from '@/components/ai-usage/ReviewAutomationWidget'
import { useAgentInfo } from '@/lib/ops'
import { FeatureGate } from '@shared/subscription'

function UpgradePrompt() {
  return (
    <div className="col-span-full flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center dark:border-gray-700 dark:bg-gray-800/50">
      <Lock className="h-8 w-8 text-gray-400" />
      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">AI Ops isn't included in your current plan</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">Upgrade to a plan with the AI chat assistant feature to unlock this.</p>
      <Link to="/billing" className="mt-1 text-sm font-medium text-blue-600 hover:underline">View plans →</Link>
    </div>
  )
}

function AiOpsPage() {
  const { data: info } = useAgentInfo()

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">AI Operations Center</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Natural-language operations over the platform — observe, diagnose, and propose actions.
          </p>
        </div>
        {info && (
          <div className="hidden md:flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-500"><Bot className="w-4 h-4" /> {info.persona ?? 'ops'}</span>
            <span className="flex items-center gap-1 text-gray-500"><Cpu className="w-4 h-4" /> {info.model}</span>
            <span className="flex items-center gap-1 text-gray-500"><ShieldCheck className="w-4 h-4" /> {info.tools_loaded ?? 0} tools</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${info.status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {info.status}
            </span>
          </div>
        )}
      </div>

      <FeatureGate feature="ai.chat" fallback={<div className="grid grid-cols-1 gap-4"><UpgradePrompt /></div>}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AiChat />
          <ReviewAutomationWidget />
        </div>

        {/* Real usage/audit data from ai-engine's Prometheus counters + Redis audit log
            (app/routers/verification.py). */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <AiUsageWidget />
          <AiAuditLogWidget />
        </div>
      </FeatureGate>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/ai-ops/')({
  component: AiOpsPage,
})
